import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { API_ROUTES } from "@/services/environment";
import { GET } from "@/services/api";
import { useAppSelector } from "@/hooks/hook";
import { IQuoteObject } from "@/interfaces";
import { Skeleton } from "@/components/skeleton/Skeleton";

interface RecentInquiriesProps {
    title?: string;
    compact?: boolean;
}

const InquiryRowSkeleton = () => (
    <div className="flex items-start justify-between gap-2 flex-wrap bg-p-gray rounded-xl px-4 py-3 border border-orange-100">
        <div className="flex flex-col gap-2">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-3 w-28" />
        </div>
        <Skeleton className="h-4 w-16" />
    </div>
);

function formatStage(stage: string | null) {
    if (!stage) return "—";
    if (stage === "inquiry") return "New Inquiry";
    return stage.charAt(0).toUpperCase() + stage.slice(1).replace(/-/g, " ");
}

export const RecentInquiries: React.FC<RecentInquiriesProps> = ({
    title = "Recent Inquiries & Leads",
    compact = false,
}) => {
    const { getPaginatedQuotesList } = API_ROUTES;
    const { refreshToken, business_id } = useAppSelector((store) => store.user);
    const { currentBusiness } = useAppSelector((store) => store.business);
    const [quotes, setQuotes] = useState<IQuoteObject[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        (async () => {
            try {
                setIsLoading(true);
                const businessId = currentBusiness ? currentBusiness : business_id;
                const res = await GET(
                    `${getPaginatedQuotesList}?pageNumber=1${businessId ? `&business_id=${businessId}` : ""
                    }`,
                    refreshToken
                );
                if (res?.data?.success) {
                    setQuotes(res.data.result);
                }
            } catch (error) {
                console.log(error);
            } finally {
                setIsLoading(false);
            }
        })();
    }, [currentBusiness, business_id]);

    return (
        <div
            className={`h-full flex flex-col min-h-0 ${compact ? "p-3" : "bg-white rounded-2xl p-5"
                }`}
        >
            <div className="flex justify-between items-center mb-3 shrink-0">
                <p className="text-xl font-semibold">{title}</p>
                <Link
                    to="/events?tab=Leads"
                    className="bg-black text-white rounded-full py-2 px-6 text-sm"
                >
                    View All
                </Link>
            </div>
            <div className="flex flex-col gap-3 flex-1 min-h-0 overflow-y-auto pr-3">
                {isLoading ? (
                    <>
                        <InquiryRowSkeleton />
                        <InquiryRowSkeleton />
                    </>
                ) : quotes.length > 0 ? (
                    quotes.map((item) => (
                        <div
                            key={item.id}
                            className="flex items-start justify-between gap-2 flex-wrap bg-p-gray rounded-xl px-4 py-3 border border-orange-300"
                        >
                            <div>
                                <p className="text-sm">
                                    <span className="text-gray-400">Customer:</span>{" "}
                                    <span className="font-semibold">
                                        {[item.first_name, item.last_name]
                                            .filter(Boolean)
                                            .join(" ") || "—"}
                                    </span>
                                </p>
                                <p className="text-xs text-gray-400">
                                    Event Date:{" "}
                                    <span className="text-gray-600">
                                        {item.start_time
                                            ? format(parseISO(item.start_time), "MMM d, yyyy")
                                            : "TBD"}
                                    </span>
                                </p>
                            </div>
                            <div className="text-right text-xs pr-3">
                                <p className="font-medium">{formatStage(item.quote_stage)}</p>
                                {/* <p className="text-gray-400">({item.event_type ?? "—"})</p> */}
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="flex-1 flex items-center justify-center">
                        <p className="text-center text-gray-500 text-sm">No Quotes</p>
                    </div>
                )}
            </div>
        </div>
    );
};
